/**
 * useSubscription Hook
 *
 * React hook for managing workspace subscription state and interactions.
 * Provides subscription details, status checks, and upgrade dialog control.
 *
 * Usage:
 * ```tsx
 * const { subscription, status, showUpgradeDialog } = useSubscription(workspaceId);
 * ```
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';
import { useToastStore } from '@/store/useToasts';

/**
 * Subscription API response workspace data
 */
interface SubscriptionWorkspace {
  id: string;
  name: string;
  stripe_subscription_id?: string | null;
  stripe_customer_id?: string | null;
  subscription_status?: string | null;
  billing_interval?: 'monthly' | 'yearly' | null;
  current_period_end?: string | null;
  canceled_at?: string | null;
  plan_id?: string | null;
}

/**
 * Subscription API response plan data
 */
export interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  monthly_price_cents?: number;
  yearly_price_cents?: number;
  max_storage_bytes?: number;
  max_tables?: number;
  max_members?: number;
}

interface UpgradeContext {
  type: 'trial_expiring' | 'trial_expired' | 'storage_limit' | 'feature_access';
  message?: string;
  daysRemaining?: number;
}

/**
 * Invoice data from Stripe
 */
export interface Invoice {
  id: string;
  number: string | null;
  status: string | null;
  amount_due: number;
  amount_paid: number;
  currency: string;
  created: number;
  period_start: number;
  period_end: number;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

/**
 * Subscription hook for managing workspace billing
 */
export function useSubscription(workspaceId: string) {
  const rpc = useRpc();
  const queryClient = useQueryClient();
  const { error: showErrorToast } = useToastStore();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState<UpgradeContext | undefined>();

  // Fetch subscription details
  const {
    data: subscriptionData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.workspace.subscription(workspaceId),
    queryFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces[':id'].subscription.$get({
        param: { id: workspaceId }
      });
      if (!res.ok) {
        throw new Error('Failed to fetch subscription');
      }
      const data = await res.json();
      // API returns { workspace, plan, status, limits }
      // TanStack Query requires non-undefined return values
      return data ?? null;
    },
    enabled: !!workspaceId,
    // Refetch every 5 minutes to catch status changes
    refetchInterval: 5 * 60 * 1000,
  });

  // Fetch invoices (lazy - only when enabled)
  const [invoicesEnabled, setInvoicesEnabled] = useState(false);
  const {
    data: invoicesData,
    isLoading: isLoadingInvoices,
    refetch: refetchInvoices,
  } = useQuery({
    queryKey: [...queryKeys.workspace.subscription(workspaceId), 'invoices'],
    queryFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces[':id'].subscription.invoices.$get({
        param: { id: workspaceId }
      });
      if (!res.ok) {
        throw new Error('Failed to fetch invoices');
      }
      const data = await res.json();
      return (data as { invoices: Invoice[] }).invoices ?? [];
    },
    enabled: !!workspaceId && invoicesEnabled,
  });

  /**
   * Fetch invoices on demand
   */
  const fetchInvoices = useCallback(() => {
    setInvoicesEnabled(true);
    if (invoicesEnabled) {
      refetchInvoices();
    }
  }, [invoicesEnabled, refetchInvoices]);

  // Billing portal mutation
  const portalMutation = useMutation({
    mutationFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces[':id'].subscription.portal.$post({
        param: { id: workspaceId }
      });
      if (!res.ok) {
        throw new Error('Failed to create portal session');
      }
      const response = await res.json();
      // Check for error response
      if ('error' in response) {
        throw new Error(response.error || 'Failed to create portal session');
      }
      // Backend returns { url } directly
      if (!response.url) {
        throw new Error('Failed to create portal session');
      }
      return response as { url: string };
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) => {
      showErrorToast(
        'Portal Error',
        err instanceof Error ? err.message : 'Failed to open billing portal'
      );
    },
  });

  // Cancel subscription mutation
  const cancelMutation = useMutation({
    mutationFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces[':id'].subscription.$delete({
        param: { id: workspaceId }
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || 'Failed to cancel subscription');
      }
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspace.subscription(workspaceId) });
      refetch();
    },
    onError: (err) => {
      showErrorToast(
        'Cancellation Error',
        err instanceof Error ? err.message : 'Failed to cancel subscription'
      );
    },
  });

  // Reactivate subscription mutation
  const reactivateMutation = useMutation({
    mutationFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces[':id'].subscription.$patch({
        param: { id: workspaceId }
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || 'Failed to reactivate subscription');
      }
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspace.subscription(workspaceId) });
      refetch();
    },
    onError: (err) => {
      showErrorToast(
        'Reactivation Error',
        err instanceof Error ? err.message : 'Failed to reactivate subscription'
      );
    },
  });

  /**
   * Cancel the current subscription
   */
  const cancelSubscription = useCallback(() => {
    cancelMutation.mutate();
  }, [cancelMutation]);

  /**
   * Reactivate a subscription that was scheduled for cancellation
   */
  const reactivateSubscription = useCallback(() => {
    reactivateMutation.mutate();
  }, [reactivateMutation]);

  /**
   * Open Stripe Customer Portal for billing management
   */
  const openBillingPortal = useCallback(() => {
    portalMutation.mutate();
  }, [portalMutation]);

  /**
   * Show upgrade dialog with optional context
   */
  const showUpgradeDialog = (context?: UpgradeContext) => {
    setUpgradeContext(context);
    setUpgradeDialogOpen(true);
  };

  /**
   * Hide upgrade dialog
   */
  const hideUpgradeDialog = () => {
    setUpgradeDialogOpen(false);
    setUpgradeContext(undefined);
  };

  /**
   * Check if subscription is active (not expired)
   */
  const isActive = subscriptionData?.status?.isActive ?? false;

  /**
   * Check if workspace has an active Stripe subscription (required for plan changes)
   * Must have BOTH: stripe_subscription_id AND active status
   * This prevents trying to change a canceled/expired subscription
   */
  const hasActiveStripeSubscription = !!(subscriptionData?.workspace as SubscriptionWorkspace | null)?.stripe_subscription_id
    && (subscriptionData?.workspace as SubscriptionWorkspace | null)?.subscription_status === 'active';

  // Keep for backwards compatibility but prefer hasActiveStripeSubscription
  const hasStripeSubscription = hasActiveStripeSubscription;

  /**
   * Check if currently in trial period
   */
  const isTrial = subscriptionData?.status?.isTrial ?? false;

  /**
   * Check if subscription is expired
   */
  const isExpired = subscriptionData?.status?.isExpired ?? false;

  /**
   * Check if payment is required (trial ending soon)
   */
  const requiresPayment = subscriptionData?.status?.requiresPayment ?? !subscriptionData?.workspace;

  /**
   * Days remaining until expiration (trial or subscription end)
   */
  const daysRemaining = subscriptionData?.status?.daysRemaining ?? null;

  /**
   * Current plan name
   */
  const plan = subscriptionData?.plan as SubscriptionPlan | null;
  const planName = plan?.name ?? 'No Plan';

  /**
   * Current plan slug
   */
  const planSlug = plan?.slug ?? null;

  return {
    // Data - workspace now contains billing fields after subscription merge
    workspace: subscriptionData?.workspace,
    plan,
    status: subscriptionData?.status,
    limits: subscriptionData?.limits,

    // Status helpers
    isActive,
    isTrial,
    isExpired,
    requiresPayment,
    daysRemaining,
    planName,
    planSlug,
    hasStripeSubscription,

    // Upgrade dialog
    showUpgradeDialog,
    hideUpgradeDialog,
    upgradeDialogOpen,
    upgradeContext,

    // Query state
    isLoading,
    error,
    refetch,

    // Billing portal
    openBillingPortal,
    isOpeningPortal: portalMutation.isPending,

    // Cancel subscription
    cancelSubscription,
    isCanceling: cancelMutation.isPending,

    // Reactivate subscription
    reactivateSubscription,
    isReactivating: reactivateMutation.isPending,

    // Invoices
    invoices: invoicesData ?? [],
    isLoadingInvoices,
    fetchInvoices,
  };
}

/**
 * Helper function to open Stripe billing portal
 * Can be used outside of the hook context
 */
export async function openStripePortal(
  rpc: ReturnType<typeof useRpc>,
  workspaceId: string
): Promise<void> {
  // @ts-expect-error - Hono RPC type inference limitation for nested routes
  const res = await rpc.api.private.workspaces[':id'].subscription.portal.$post({
    param: { id: workspaceId }
  });
  if (!res.ok) {
    throw new Error('Failed to create portal session');
  }
  const response = await res.json();
  // Check for error response
  if ('error' in response) {
    throw new Error(response.error || 'Failed to create portal session');
  }
  // Backend returns { url } directly
  if (response.url) {
    window.location.href = response.url;
  } else {
    throw new Error('Failed to create portal session');
  }
}
